import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { useNavigate } from 'react-router-dom'
import { colors, radius, shadow, s, ui } from '../styles/theme'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const st = {
  card: { background: colors.bgCard, borderRadius: radius.lg, border: `1px solid ${colors.border}`, boxShadow: shadow.sm, marginBottom: '16px' },
  head: { padding: '14px 18px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' },
  title: { ...s.sectionTitle, margin: 0 },
  th: { ...s.th, whiteSpace: 'nowrap' },
  td: { ...s.td, verticalAlign: 'top' },
  empty: { ...ui.emptyText, padding: '20px', textAlign: 'center' }
}

function Badge({ text, tone }) {
  return <span style={tone === 'danger' ? { ...ui.chip, color: colors.dangerText } : ui.chip}>{text}</span>
}
function StatusBadge({ status }) {
  if (status === 'ok') return <Badge text="Başarılı" />
  if (status === 'error') return <Badge text="Hata" tone="danger" />
  return <Badge text={status} />
}
function timeAgo(ts) {
  if (!ts) return '—'
  const diff = Date.now() - new Date(ts).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'az önce'
  if (m < 60) return m + ' dk önce'
  const h = Math.floor(m / 60)
  if (h < 24) return h + ' saat önce'
  return Math.floor(h / 24) + ' gün önce'
}
function dur(ms) {
  if (ms == null) return '—'
  if (ms < 1000) return ms + ' ms'
  if (ms < 60000) return (ms / 1000).toFixed(1) + ' sn'
  return Math.floor(ms / 60000) + ' dk ' + Math.round((ms % 60000) / 1000) + ' sn'
}
function Section({ title, right, children }) {
  return (
    <div style={st.card}>
      <div style={st.head}><h3 style={st.title}>{title}</h3>{right}</div>
      {children}
    </div>
  )
}
function Table({ head, children, empty }) {
  return (
    <div className="sx-scroll-x" style={{ overflowX: 'auto' }}>
      <table className={head.length >= 5 ? 'sx-cards' : undefined} style={{ width: '100%', borderCollapse: 'collapse', minWidth: 'min(520px, 100%)', maxWidth: 'none' }}>
        <thead><tr>{head.map((h, i) => <th key={i} style={st.th}>{h}</th>)}</tr></thead>
        <tbody>{React.Children.count(children) ? children : <tr><td colSpan={head.length} style={st.empty}>{empty || 'Kayıt yok.'}</td></tr>}</tbody>
      </table>
    </div>
  )
}

export default function SystemMonitor() {
  const { account } = useBrand()
  const navigate = useNavigate()
  const [d, setD] = useState(null)
  const [loading, setLoading] = useState(true)
  const token = localStorage.getItem('adminToken')
  const headers = { Authorization: token }

  function load() {
    setLoading(true)
    axios.get(`${API}/admin/system-monitor`, { headers })
      .then(r => { setD(r.data); setLoading(false) })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    if (account?.role !== 'super_admin') { navigate('/'); return }
    load()
  }, []) // eslint-disable-line

  if (loading) return <p style={ui.emptyText}>Yükleniyor…</p>
  if (!d) return <p style={ui.emptyText}>Veri alınamadı.</p>

  const scheduleByName = {}
  ;(d.schedule || []).forEach(s => { scheduleByName[s.name] = s })

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ ...s.pageTitle, margin: '0 0 4px' }}>Sistem Monitörü</h2>
          <p style={s.pageSubtitle}>Tüm markaların sistem hareketleri — job'lar, mailing, senkron, hatalar.</p>
        </div>
        <button onClick={load} style={s.btnSecondary}>Yenile</button>
      </div>

      {/* Hata akışı — en üstte, dikkat çekici */}
      <Section title={`Hata Akışı${d.errors.length ? ` (${d.errors.length})` : ''}`}>
        <Table head={['Job', 'Marka', 'Hata', 'Zaman']} empty="Son kayıtlarda hata yok.">
          {d.errors.map((e, i) => (
            <tr key={i}>
              <td style={st.td}><b>{e.job_name}</b></td>
              <td style={st.td}>{e.brand_name || '—'}</td>
              <td className="sx-break" style={{ ...st.td, color: colors.dangerText, maxWidth: '420px', minWidth: '200px', whiteSpace: 'normal' }}>{e.error}</td>
              <td style={{ ...st.td, whiteSpace: 'nowrap', color: colors.textSecondary }}>{timeAgo(e.finished_at)}</td>
            </tr>
          ))}
        </Table>
      </Section>

      {/* Zamanlanmış görevler — program */}
      <Section title="Zamanlanmış Görevler — Program">
        <Table head={['Görev', 'Zaman', 'Açıklama']}>
          {d.schedule.map((s, i) => (
            <tr key={i}>
              <td style={st.td}><b>{s.name}</b></td>
              <td style={{ ...st.td, whiteSpace: 'nowrap', color: colors.textSecondary }}>{s.time}</td>
              <td style={{ ...st.td, color: colors.textSecondary, minWidth: '200px', whiteSpace: 'normal' }}>{s.desc}</td>
            </tr>
          ))}
        </Table>
      </Section>

      {/* Son job çalışmaları */}
      <Section title="Son Job Çalışmaları">
        <Table head={['Job', 'Marka', 'Durum', 'Özet', 'Süre', 'Zaman']} empty="Henüz job çalışması loglanmadı (bir sonraki cron'da dolar).">
          {d.jobs.map((j, i) => (
            <tr key={i}>
              <td data-label="Job" style={st.td}><b>{j.job_name}</b></td>
              <td data-label="Marka" style={st.td}>{j.brand_name || '—'}</td>
              <td data-label="Durum" style={st.td}><StatusBadge status={j.status} /></td>
              <td className="sx-break" data-label="Özet" style={{ ...st.td, color: colors.textSecondary, maxWidth: '320px', minWidth: '200px', whiteSpace: 'normal' }}>{j.status === 'error' ? <span style={{ color: colors.dangerText }}>{j.error}</span> : (j.summary || '—')}</td>
              <td data-label="Süre" style={{ ...st.td, ...ui.numMuted, whiteSpace: 'nowrap' }}>{dur(j.duration_ms)}</td>
              <td data-label="Zaman" style={{ ...st.td, whiteSpace: 'nowrap', color: colors.textSecondary }}>{timeAgo(j.finished_at)}</td>
            </tr>
          ))}
        </Table>
      </Section>

      {/* Mailing gönderimleri */}
      <Section title="Smart Mailing Gönderimleri — Son 30 Gün">
        <Table head={['Marka', 'Senaryo', 'Gönderim', 'Son gönderim']} empty="Son 30 günde gönderim yok.">
          {d.mailing.map((m, i) => (
            <tr key={i}>
              <td style={st.td}>{m.brand_name}</td>
              <td style={st.td}><b>{m.scenario}</b></td>
              <td style={{ ...st.td, ...ui.num }}>{Number(m.count).toLocaleString('tr-TR')}</td>
              <td style={{ ...st.td, whiteSpace: 'nowrap', color: colors.textSecondary }}>{timeAgo(m.last_sent)}</td>
            </tr>
          ))}
        </Table>
      </Section>

      {/* Ürün senkron durumu */}
      <Section title="Ürün Senkron Durumu">
        <Table head={['Marka', 'Ürün', 'Son senkron']} empty="Veri yok.">
          {d.sync.map((s, i) => {
            const stale = !s.last_synced || (Date.now() - new Date(s.last_synced).getTime()) > 26 * 3600 * 1000
            return (
              <tr key={i}>
                <td style={st.td}>{s.brand_name}</td>
                <td style={{ ...st.td, ...ui.num }}>{Number(s.product_count).toLocaleString('tr-TR')}</td>
                <td style={{ ...st.td, whiteSpace: 'nowrap', color: stale ? colors.dangerText : colors.textSecondary, fontWeight: stale ? 600 : 400 }}>{timeAgo(s.last_synced)}</td>
              </tr>
            )
          })}
        </Table>
      </Section>

      {/* Smart Ranking */}
      <Section title="Smart Ranking Çalışmaları">
        <Table head={['Marka', 'Aktif', 'Otomatik', 'Son çalışma', 'Yazılan', 'Stoksuz', 'Hata']} empty="Yapılandırılmış marka yok.">
          {d.ranking.map((r, i) => (
            <tr key={i}>
              <td data-label="Marka" style={st.td}>{r.brand_name}</td>
              <td data-label="Aktif" style={st.td}><span style={ui.status(!!r.is_active)}><span style={ui.statusDot(!!r.is_active)} />{r.is_active ? 'Aktif' : 'Pasif'}</span></td>
              <td data-label="Otomatik" style={{ ...st.td, color: colors.textSecondary }}>{r.auto_schedule ? '✓' : '—'}</td>
              <td data-label="Son çalışma" style={{ ...st.td, whiteSpace: 'nowrap', color: colors.textSecondary }}>{r.last_run_at ? timeAgo(r.last_run_at) : 'hiç'}</td>
              <td data-label="Yazılan" style={{ ...st.td, ...ui.num }}>{r.last_run_count != null ? Number(r.last_run_count).toLocaleString('tr-TR') : '—'}</td>
              <td data-label="Stoksuz" style={{ ...st.td, ...ui.numMuted }}>{r.last_run_outstock != null ? Number(r.last_run_outstock).toLocaleString('tr-TR') : '—'}</td>
              <td data-label="Hata" style={{ ...st.td, ...ui.numMuted, color: r.last_run_fail ? colors.dangerText : colors.textSecondary }}>{r.last_run_fail != null ? r.last_run_fail : '—'}</td>
            </tr>
          ))}
        </Table>
      </Section>

      <div className="sx-grid-panel" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '16px' }}>
        {/* CIP senkron */}
        <Section title="CIP Senkron">
          <Table head={['Marka', 'Profil sayısı']} empty="CIP profili yok.">
            {d.cip.map((c, i) => (
              <tr key={i}>
                <td style={st.td}>{c.brand_name}</td>
                <td style={{ ...st.td, ...ui.num }}>{Number(c.profiles).toLocaleString('tr-TR')}</td>
              </tr>
            ))}
          </Table>
        </Section>

        {/* Marka & modül */}
        <Section title="Marka & Modül Durumu">
          <Table head={['Marka', 'Durum', 'Aktif modüller']} empty="Marka yok.">
            {d.brands.map((b, i) => (
              <tr key={i}>
                <td style={st.td}>{b.name}</td>
                <td style={st.td}><span style={ui.status(!!b.is_active)}><span style={ui.statusDot(!!b.is_active)} />{b.is_active ? 'Aktif' : 'Pasif'}</span></td>
                <td style={{ ...st.td, minWidth: '180px', whiteSpace: 'normal' }}>
                  {b.modules.length ? b.modules.map(m => <span key={m} style={{ ...ui.chip, margin: '0 4px 4px 0' }}>{m}</span>) : <span style={{ color: colors.textMuted }}>—</span>}
                </td>
              </tr>
            ))}
          </Table>
        </Section>
      </div>

      {/* Widget canlılığı */}
      <Section title="Widget Canlılığı — Arama & Event">
        <Table head={['Marka', 'Arama (24s)', 'Arama (7g)', 'Event (24s)', 'Event (7g)']} empty="Veri yok.">
          {d.widget.map((w, i) => (
            <tr key={i}>
              <td data-label="Marka" style={st.td}>{w.brand_name}</td>
              <td data-label="Arama (24s)" style={{ ...st.td, ...ui.num }}>{Number(w.search_24h).toLocaleString('tr-TR')}</td>
              <td data-label="Arama (7g)" style={{ ...st.td, ...ui.numMuted }}>{Number(w.search_7d).toLocaleString('tr-TR')}</td>
              <td data-label="Event (24s)" style={{ ...st.td, ...ui.num }}>{Number(w.event_24h).toLocaleString('tr-TR')}</td>
              <td data-label="Event (7g)" style={{ ...st.td, ...ui.numMuted }}>{Number(w.event_7d).toLocaleString('tr-TR')}</td>
            </tr>
          ))}
        </Table>
      </Section>
    </div>
  )
}
